import { useCallback, useEffect, useState } from 'react';
import sessionStorageService from 'services/sessionStorageService';
import { AiDraftBannerStep, getDismissedBannersStorageKey } from './utils';

type DismissedSteps = Partial<Record<AiDraftBannerStep, boolean>>;

const getDismissedSteps = (listingId: string) =>
  sessionStorageService.getItem<DismissedSteps>(
    getDismissedBannersStorageKey(listingId),
  ) || {};

// Dismissals last for the rest of the browser session, so the banner stays gone when
// the broker navigates back to the step - or reloads - but returns in a new session.
export default function useAiDraftBannerDismissal(
  listingId: string,
  step: AiDraftBannerStep,
) {
  const [dismissedSteps, setDismissedSteps] = useState<DismissedSteps>(() =>
    getDismissedSteps(listingId),
  );

  // listingId is already there on mount - ListingProvider blocks its children until
  // the listing loads - but re-read if the wizard ever swaps listings in place.
  useEffect(() => {
    setDismissedSteps(getDismissedSteps(listingId));
  }, [listingId]);

  const dismiss = useCallback(() => {
    const updated = { ...getDismissedSteps(listingId), [step]: true };

    sessionStorageService.setItem(
      getDismissedBannersStorageKey(listingId),
      updated,
    );
    setDismissedSteps(updated);
  }, [listingId, step]);

  return { isDismissed: !!dismissedSteps[step], dismiss };
}
